<script setup lang="ts">
import { useTemplateRef } from 'vue';

// A pure text-field atom: the recurring field recipe (surface-raised fill, subtle
// strong border, radius, and a visible focus ring) extracted once. `v-model`
// compatible via `modelValue` + `update:modelValue`. It knows nothing of any
// feature — id, name, aria-*, placeholder, maxlength, autocomplete all fall
// through as attrs. Styled entirely from SD-052 semantic tokens (the guardrail
// governs this file).
//
// Only the shared box is owned here; each consumer keeps its own padding/colour/
// width on its retained class, disjoint from these props, so adoption stays
// pixel-neutral without a cascade-order fight (the [[Surface]] pattern).
withDefaults(
  defineProps<{
    modelValue: string;
    /** Native input `type`. Defaults to `text`. */
    type?: string;
    disabled?: boolean;
  }>(),
  { type: 'text', disabled: false }
);

const emit = defineEmits<{ 'update:modelValue': [string] }>();

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}

const el = useTemplateRef<HTMLInputElement>('el');

// The one imperative affordance the atom hands out (SD-065 ticket 07): the raw
// element lives in here, so a consumer that must move focus to an errored field
// on invalid submit — the pattern Login/SetPassword do directly on their raw
// `<input>` — has no other route to it. Exposing `focus()` keeps the element
// itself encapsulated.
defineExpose({
  focus: (): void => el.value?.focus(),
});
</script>

<template>
  <input
    ref="el"
    class="input"
    :type="type"
    :value="modelValue"
    :disabled="disabled"
    @input="onInput"
  />
</template>

<style scoped>
.input {
  font: inherit;
  font-size: var(--font-size-sm);
  background: var(--color-surface-raised);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
}

.input:focus-visible {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 3px var(--color-focus-ring);
}

.input:disabled {
  cursor: not-allowed;
  color: var(--color-text-muted);
  background: var(--color-surface-sunken);
}
</style>
